import { ChemistrySample } from "@/pages/caster/models/ElementData";
import { getMomentTz } from "@dexteel/mesf-core";
import {
	CellClassParams,
	ColDef,
	ColGroupDef,
	RowClassParams,
	RowStyle,
	ValueFormatterParams,
	ValueGetterParams,
} from "ag-grid-community";
import { useMemo } from "react";

interface Props {
	elementList: ChemistrySample[];
}

const moment = getMomentTz();

// Transformed data structure for table rows
interface TransformedRowData {
	id: string;
	SampleTypeName: string | null;
	SampleSequence: number | null;
	Reception: Date | null;
	[key: string]:
		| {
				value: number | null;
				status?: "Above" | "Below" | "Within" | "Info" | null;
				minValue?: number | null;
				maxValue?: number | null;
				decimals: number | null;
		  }
		| string
		| number
		| Date
		| null;
}

// Type aliases for intermediate objects
type UniqueElement = {
	order: number;
	decimals: number;
};
type UniqueElementsMap = Record<string, UniqueElement>;
type SamplesMap = Record<string, TransformedRowData>;
type LimitsMap = Record<string, number | null>;

export const useElementTableData = ({ elementList }: Props) => {
	const { rowData, chemistryGroupCol } = useMemo(() => {
		// Maps to store unique elements, grouped samples and min/max limits
		const uniqueElements: UniqueElementsMap = {};
		const samplesMap: SamplesMap = {};
		const minLimits: LimitsMap = {};
		const maxLimits: LimitsMap = {};

		// Process each sample and group data (only if elementList is not empty)
		if (elementList && elementList.length > 0) {
			elementList.forEach((sample) => {
				const sampleId = `${sample.SampleTypeName || "?"}${
					sample.SampleSequence ?? ""
				}`;
				const elementName = sample.ElementName;
				if (!elementName) return;

				// Register unique element info
				if (!uniqueElements[elementName]) {
					uniqueElements[elementName] = {
						order: sample.Order ?? Infinity,
						decimals: sample.Decimals ?? 4,
					};
					minLimits[elementName] = null;
					maxLimits[elementName] = null;
				}

				// Update min and max limits
				if (sample.MinValue !== null) {
					if (
						minLimits[elementName] === null ||
						sample.MinValue < minLimits[elementName]!
					) {
						minLimits[elementName] = sample.MinValue;
					}
				}
				if (sample.MaxValue !== null) {
					if (
						maxLimits[elementName] === null ||
						sample.MaxValue > maxLimits[elementName]!
					) {
						maxLimits[elementName] = sample.MaxValue;
					}
				}

				// Group sample by sampleId
				if (!samplesMap[sampleId]) {
					samplesMap[sampleId] = {
						id: sampleId,
						SampleTypeName: sample.SampleTypeName || "",
						SampleSequence: sample.SampleSequence ?? null,
						Reception: sample.Reception || null,
					};
				}
				samplesMap[sampleId][elementName] = {
					value: sample.Value,
					status: sample.Status,
					minValue: sample.MinValue,
					maxValue: sample.MaxValue,
					decimals: uniqueElements[elementName].decimals,
				};
			});
		}

		// Order elements by 'order' property and then alphabetically
		// If elementList was empty, uniqueElements is empty, sortedElements is []
		const sortedElements = Object.keys(uniqueElements).sort((a, b) => {
			const orderA = uniqueElements[a].order;
			const orderB = uniqueElements[b].order;
			return orderA !== orderB ? orderA - orderB : a.localeCompare(b);
		});

		// --- Define static columns here (always defined) ---
		const staticColumns: ColDef[] = [
			{
				headerName: "Sample",
				valueGetter: (params: ValueGetterParams<TransformedRowData>) => {
					if (!params.data) return "";
					return params.data.id;
				},
				minWidth: 100,
				flex: 1,
			},
			{
				headerName: "Time",
				minWidth: 81,
				resizable: true,
				valueGetter: (params: ValueGetterParams<TransformedRowData>) => {
					if (
						!params.data ||
						params.data.id === "MIN" ||
						params.data.id === "MAX"
					)
						return "";
					return params.data.Reception
						? moment(params.data.Reception).format("HH:mm")
						: "";
				},
				flex: 1,
				cellStyle: {
					textAlign: "center",
				},
			},
		];

		// Dynamic column definitions for each element
		const dynamicColumns: ColDef[] = sortedElements.map((elementName) => {
			const defaultDecimals = uniqueElements[elementName].decimals;
			return {
				headerName: elementName,
				minWidth: 80,
				flex: 1,
				valueGetter: (params: ValueGetterParams<TransformedRowData>) => {
					if (!params.data) return null;
					const colData = params.data[elementName];
					if (colData && typeof colData === "object") {
						return (colData as { value: number | null }).value;
					}
					return null;
				},
				valueFormatter: (params: ValueFormatterParams<TransformedRowData>) => {
					if (params.value === null || params.value === undefined) return "-";
					let decimals = defaultDecimals;
					if (params.data) {
						const colData = params.data[elementName];
						if (colData && typeof colData === "object") {
							decimals = (colData as any).decimals ?? defaultDecimals;
						}
					}
					return typeof params.value === "number"
						? params.value.toFixed(decimals)
						: "-";
				},
				cellClassRules: {
					"cell-below-min": (
						params: CellClassParams<TransformedRowData>,
					): boolean => {
						if (
							params.rowIndex !== 0 ||
							!params.data ||
							params.data.id === "MIN" ||
							params.data.id === "MAX"
						)
							return false;
						const colData = params.data[elementName];
						return !!(
							colData &&
							typeof colData === "object" &&
							(colData as any).status === "Below"
						);
					},
					"cell-above-max": (
						params: CellClassParams<TransformedRowData>,
					): boolean => {
						if (
							params.rowIndex !== 0 ||
							!params.data ||
							params.data.id === "MIN" ||
							params.data.id === "MAX"
						)
							return false;
						const colData = params.data[elementName];
						return !!(
							colData &&
							typeof colData === "object" &&
							(colData as any).status === "Above"
						);
					},
				},
				type: "numericColumn",
			};
		});

		// Create the "Chemistry" ColGroupDef (always created)
		const chemistryGroup: ColGroupDef = {
			headerName: "Chemistry",
			children: [...staticColumns, ...dynamicColumns], // dynamicColumns is [] if no elements
		};

		// Build final row data from grouped samples
		// If elementList was empty, samplesMap is empty, finalRowData is []
		const finalRowData: TransformedRowData[] = Object.values(samplesMap);

		// Add MIN and MAX rows only if there are elements
		if (sortedElements.length > 0) {
			// Create MIN and MAX rows
			const minRow: TransformedRowData = {
				id: "MIN",
				SampleTypeName: "MIN",
				SampleSequence: null,
				Reception: null,
			};
			const maxRow: TransformedRowData = {
				id: "MAX",
				SampleTypeName: "MAX",
				SampleSequence: null,
				Reception: null,
			};

			// Populate MIN and MAX rows with respective limits
			sortedElements.forEach((elementName) => {
				const defaultDecimals = uniqueElements[elementName].decimals;
				minRow[elementName] = {
					value: minLimits[elementName],
					decimals: defaultDecimals,
				};
				maxRow[elementName] = {
					value: maxLimits[elementName],
					decimals: defaultDecimals,
				};
			});

			finalRowData.push(minRow, maxRow);
		}

		// Always return a valid group definition and the (potentially empty) row data
		return { rowData: finalRowData, chemistryGroupCol: chemistryGroup };
	}, [elementList]);

	// Row style for MIN/MAX rows
	const getRowStyle = (params: RowClassParams): RowStyle =>
		params.data && (params.data.id === "MIN" || params.data.id === "MAX")
			? { backgroundColor: "#f0f0f0" }
			: {};

	const columns: (ColDef | ColGroupDef)[] = useMemo(
		() => (chemistryGroupCol ? chemistryGroupCol.children || [] : []),
		[chemistryGroupCol], // Dependency on the object returned by the main useMemo
	);

	// Default column configuration
	const defaultColDef: ColDef = useMemo(
		() => ({
			sortable: false,
			editable: false,
			suppressMenu: true,
			flex: 1,
			suppressHeaderMenuButton: true,
		}),
		[],
	);

	return {
		columns,
		defaultColDef,
		rowData,
		getRowStyle,
	};
};
